import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { EventSheet } from "../ui/EventSheet";
import {
  daysFromNow,
  eventWhenText,
  usePastEvents,
  useUnlinkedRegistrations,
  useUpcomingEvents,
  type CrmEvent,
} from "../data";
import { linkRegistrations } from "../actions";
import { NOUNS, count } from "../hebrew";
import { IcAlert, IcCalendar, IcChevron, IcPeople, IcPlus, IcTent } from "../ui/icons";

/**
 * Events, in three parts: what is coming, what needs attaching, and what already
 * happened.
 *
 * The middle one is the part that would not exist in a normal calendar app, and
 * it is the reason this screen was blocking three other things. Registration
 * forms on the website post a page slug; the event usually does not exist yet, so
 * the sign-ups arrive holding a string that matches nothing. Those people signed
 * up and are waiting on a form nobody has read, and until now there was nowhere
 * that fact could be seen.
 */
export function EventsPage() {
  const { role } = useAuth();
  const isStaff = role !== "viewer";

  const upcoming = useUpcomingEvents();
  const past = usePastEvents();
  const unlinked = useUnlinkedRegistrations();

  const [creating, setCreating] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [linking, setLinking] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // A deletion navigates back here, and its outcome is the only place the count
  // of unlinked registrations is reported. Read once from the navigation state so
  // it survives the redirect but not a refresh.
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;

  /** The waiting sign-ups, grouped by the slug they are asking for. */
  const bySlug = new Map<string, number>();
  for (const r of unlinked.data) {
    const slug = r.eventSlug ?? "";
    if (slug === "") continue;
    bySlug.set(slug, (bySlug.get(slug) ?? 0) + 1);
  }

  async function runLink() {
    setLinking(true);
    setNote(null);
    try {
      const r = await linkRegistrations();
      const stillWaiting = Object.entries(r.unmatchedSlugs);
      setNote(
        `חיברנו ${r.linked} הרשמות לאירועים.` +
          (stillWaiting.length > 0
            ? ` עדיין מחכות: ${stillWaiting.map(([s, n]) => `${s} (${n})`).join(", ")}. צרו את האירועים האלה.`
            : " לא נשארו הרשמות בלי אירוע."),
      );
    } catch (e) {
      setNote(e instanceof Error ? e.message : "לא הצלחנו לחבר. נסו שוב.");
    } finally {
      setLinking(false);
    }
  }

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>אירועים</h1>
            <p className="sub">
              {upcoming.loading
                ? "טוען…"
                : upcoming.data.length === 0
                  ? "אין אירועים מתוכננים"
                  : `${count(upcoming.data.length, NOUNS.event)} מתוכננים`}
            </p>
          </div>
          {isStaff && (
            <div className="hdr-actions">
              <button className="icon-btn" aria-label="אירוע חדש" onClick={() => setCreating(true)}>
                <IcPlus size={20} />
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="body">
        {flash && <div className="notice info">{flash}</div>}
        {note && <div className="notice info">{note}</div>}

        {upcoming.error && (
          <div className="notice error">
            לא הצלחנו לטעון את האירועים. זו תקלה, לא סימן שאין אירועים.
            <br />
            <span className="faint faint-xs mono">
              {upcoming.error}
            </span>
          </div>
        )}

        {/* Sign-ups with no event. Named plainly, because the number is people. */}
        {bySlug.size > 0 && (
          <section className="section">
            <div className="section-head">
              <div className="section-ico tint-red">
                <IcAlert size={17} />
              </div>
              <div className="section-title">
                הרשמות שמחכות לאירוע <span className="section-n">{unlinked.data.length}</span>
              </div>
            </div>
            <div className="card">
              <div style={{ padding: "12px 14px" }}>
                <p className="muted">
                  אנשים נרשמו דרך טופס באתר, אבל האירוע שהטופס מפנה אליו עדיין לא קיים
                  במערכת. צרו אירוע עם המזהה המתאים, וההרשמות יתחברו אליו אוטומטית.
                </p>
                <div className="chips" style={{ marginTop: 10 }}>
                  {[...bySlug.entries()].map(([slug, n]) => (
                    <span key={slug} className="tagchip" dir="ltr">
                      {slug} · {n}
                    </span>
                  ))}
                </div>
                {isStaff && (
                  <div className="btn-row" style={{ marginTop: 12 }}>
                    <button className="btn" onClick={runLink} disabled={linking}>
                      {linking ? "מחברים…" : "נסו לחבר עכשיו"}
                    </button>
                    <button className="btn btn-primary" onClick={() => setCreating(true)}>
                      <IcPlus size={15} /> אירוע חדש
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {upcoming.loading ? (
          <>
            <div className="skeleton" style={{ height: 86, marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 86 }} />
          </>
        ) : upcoming.data.length === 0 ? (
          <div className="empty">
            <div className="empty-ico tint-green">
              <IcTent size={26} />
            </div>
            <h3>אין אירועים מתוכננים</h3>
            <p>
              באירוע נשמרים התאריך, הנרשמים ומי שהגיע. בזכותו הרשמות מהאתר מתחברות
              למקום הנכון, וכרטיס מופיע במסך היום כשהאירוע מתקרב.
            </p>
            {isStaff && (
              <button className="btn btn-primary" onClick={() => setCreating(true)} style={{ marginTop: 4 }}>
                <IcPlus size={16} /> אירוע ראשון
              </button>
            )}
          </div>
        ) : (
          <section className="section">
            <div className="section-head">
              <div className="section-ico tint-orange">
                <IcCalendar size={17} />
              </div>
              <div className="section-title">מה מתקרב</div>
            </div>
            {upcoming.data.map((ev) => (
              <EventCard key={ev.id} event={ev} />
            ))}
          </section>
        )}

        {past.data.length > 0 && (
          <section className="section">
            <button className="btn btn-wide" onClick={() => setShowPast((v) => !v)}>
              {showPast ? "הסתרה" : `אירועים שהיו (${past.data.length})`}
            </button>
            {showPast && (
              <div style={{ marginTop: 10 }}>
                {past.data.map((ev) => (
                  <EventCard key={ev.id} event={ev} past />
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {creating && <EventSheet onClose={() => setCreating(false)} />}
    </div>
  );
}

function EventCard({ event, past }: { event: CrmEvent; past?: boolean }) {
  const days = daysFromNow(event.startAt);
  const cancelled = event.status === "cancelled";
  const registered = event.counts?.registered ?? 0;
  const guests = event.counts?.guests ?? 0;
  const attended = event.counts?.attendedPeople ?? 0;

  const soon = !past && !cancelled && days !== null && days <= 3;

  return (
    <Link
      to={`/events/${event.id}`}
      className="card"
      style={{
        display: "block",
        marginBottom: 10,
        opacity: cancelled ? 0.6 : 1,
        borderColor: soon ? "var(--orange)" : undefined,
      }}
    >
      <div className="card-body row" style={{ alignItems: "flex-start" }}>
        <div
          className="evt-thumb"
          style={{ flexShrink: 0, background: soon ? "var(--orange-soft)" : "var(--slate-soft)" }}
        >
          <IcCalendar size={22} />
        </div>
        <div className="grow">
          <p className="name">
            {event.title}
            {cancelled && <span className="rec-badge rec-duplicate" style={{ marginInlineStart: 8 }}>בוטל</span>}
          </p>
          <p className="muted">{eventWhenText(event)}</p>
          {event.hebrewLabel && <p className="faint">{event.hebrewLabel}</p>}
          {/* A repeating event looks identical to a one-off in a list until it
              says so, and then the list of twelve Fridays makes sense. */}
          {event.recurrenceText && (
            <span className="rec-badge rec-business" style={{ marginTop: 4 }}>
              {event.recurrenceText}
            </span>
          )}
          {event.location?.name && <p className="faint">{event.location.name}</p>}

          <p className="meta" style={{ marginTop: 6 }}>
            <span className="dot" style={{ background: soon ? "var(--orange)" : "var(--slate)" }} />
            {/* Guests as well as registrations: 24 sign-ups and 71 people are
                different rooms, and the second is the one you order food for. */}
            {count(registered, NOUNS.registration, "אין הרשמות")}
            {guests > 0 && ` · ${count(guests, NOUNS.soul)}`}
            {past && attended > 0 && ` · ${attended} הגיעו`}
          </p>
        </div>
        <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>
          <IcChevron size={17} />
        </span>
      </div>
    </Link>
  );
}

export const EVENTS_ICON = IcPeople;
